'use client';

import {useEffect, useMemo, useRef} from 'react';
import Select, {components} from 'react-select';
import PropTypes from 'prop-types';
import {
  charcoal,
  cottonCandy,
  emeraldWebText,
  fog,
  ghost,
  silver,
} from '../sass/base/_exports.sass';
import {CheckmarkIcon} from '../svg/CheckmarkIcon';
import {useMatchQuery} from '../hooks/useMatchQuery';

const DATA_QA_ID = 'data-qa-id';

const DownChevron = ({innerProps, ...props}) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <components.DownChevron
    {...props}
    innerProps={{...innerProps, [DATA_QA_ID]: 'meganavSearchCategoryDropdownDownChevron'}}
  />
);

const IndicatorsContainer = ({innerProps, ...props}) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <components.IndicatorsContainer
    {...props}
    innerProps={{...innerProps, [DATA_QA_ID]: 'meganavSearchCategoryDropdownContainer'}}
  />
);

const DropdownIndicator = ({innerProps, ...props}) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <components.DropdownIndicator
    {...props}
    innerProps={{...innerProps, [DATA_QA_ID]: 'meganavSearchCategoryDropdown'}}
  />
);

const ValueContainer = ({innerProps, ...props}) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <components.ValueContainer
    {...props}
    innerProps={{...innerProps, [DATA_QA_ID]: 'meganavSearchCategorySelector'}}
  />
);

const SearchControl = ({innerProps, ...props}) => (
  // eslint-disable-next-line react/destructuring-assignment,react/jsx-props-no-spreading
  <components.Control
    {...props}
    innerProps={{...innerProps, [DATA_QA_ID]: props.selectProps.categoryPickerDataQaId}}
  />
);

export const SearchDropdown = ({
  categories,
  selectedCategory,
  onCategorySwitch,
  categoryPickerDataQaId,
  isInline,
}) => {
  const {matches: lgWindow} = useMatchQuery('(min-width: 80rem)', []);
  const controlRef = useRef(null);

  // eslint-disable-next-line react/no-unstable-nested-components
  const CategoryOption = (optionProps) => {
    const {label} = optionProps;
    const isSelected = label === selectedCategory;

    return (
      <components.Option
        {...optionProps}
        className={`search-select-option${label === categories[0] ? ' no-separator' : ''}`}
        innerProps={{
          // eslint-disable-next-line react/destructuring-assignment
          ...optionProps.innerProps,
          'data-qa-id': `searchCategory_${label.replaceAll(' ', '_')}`,
        }}
        isSelected={isSelected}
      >
        {label}
        {isSelected && <CheckmarkIcon ratio={0.5} color={emeraldWebText} />}
      </components.Option>
    );
  };

  useEffect(() => {
    if (controlRef.current) {
      controlRef.current.onControlTouchEnd = () => {
        controlRef.current.userIsDragging = true;
      };
    }
  }, [controlRef]);

  const selectStyles = useMemo(
    () => ({
      indicatorSeparator: () => ({}),
      dropdownIndicator: (provided) => ({
        ...provided,
        color: charcoal,
      }),
      placeholder: (provided) => ({
        ...provided,
        color: charcoal,
      }),
      container: (provided) => ({
        ...provided,
        minWidth: lgWindow ? '12rem' : '9rem',
        width: lgWindow ? '12rem' : '9rem',
        height: isInline ? '3.5rem' : '3rem',
      }),
      control: (provided, {isDisabled}) => ({
        ...provided,
        height: '100%',
        paddingLeft: '1rem',
        paddingRight: 7,
        fontFamily: 'Brut',
        fontWeight: 400,
        fontSize: '1rem',
        lineHeight: '1.25rem',
        borderColor: silver,
        borderWidth: 'thin',
        boxShadow: undefined,
        backgroundColor: isDisabled ? fog : ghost,
        borderRadius: isInline ? '0.5rem' : '0.25rem',
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        borderRight: 'none',
      }),
      valueContainer: (provided) => ({
        ...provided,
        padding: 0,
        justifyContent: isInline ? 'center' : undefined,
        flex: isInline ? 'unset' : 1,
      }),
      singleValue: (provided) => ({
        ...provided,
        justifySelf: isInline ? 'center' : 'flex-start',
      }),
      menu: (provided) => ({
        ...provided,
        borderRadius: '0.5rem',
      }),
      menuList: (provided) => ({
        ...provided,
        margin: 0,
        padding: 0,
        borderRadius: '0.5rem',
      }),
      option: (provided, {isSelected}) => ({
        ...provided,
        fontFamily: 'Brut',
        fontWeight: 400,
        color: isSelected ? emeraldWebText : charcoal,
        backgroundColor: ghost,
        ':hover': {
          backgroundColor: cottonCandy,
        },
        fontSize: '1rem',
        lineHeight: '1.25rem',
      }),
    }),
    [isInline, lgWindow]
  );

  return (
    <div className="search-dropdown">
      <Select
        categoryPickerDataQaId={categoryPickerDataQaId}
        components={{
          Option: CategoryOption,
          Control: SearchControl,
          ValueContainer,
          IndicatorsContainer,
          DropdownIndicator,
          DownChevron,
        }}
        defaultValue={selectedCategory}
        isSearchable={false}
        onChange={(newValue) => onCategorySwitch(newValue.value)}
        options={categories.map((c) => ({label: c, value: c}))}
        placeholder={selectedCategory}
        ref={controlRef}
        styles={selectStyles}
      />
    </div>
  );
};

SearchDropdown.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedCategory: PropTypes.string,
  onCategorySwitch: PropTypes.func,
  categoryPickerDataQaId: PropTypes.string,
  isInline: PropTypes.bool,
};
